/**
 * AppShell — the mobile-first frame for the personal Monitor app (`/app/*`).
 *
 * Phone (default): a fixed bottom tab bar with 44px touch targets.
 * Tablet/desktop (≥768px): the same nav becomes a left side rail and the
 * content centers in a comfortable column.
 *
 * Guards the inner screens: if there's no saved token, bounce to `/app/start`.
 * The onboarding screens (`/app`, `/app/start`) render WITHOUT this shell.
 */

import { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Shield, Wallet, Bell, User, Download, X } from 'lucide-react';

import { getMonitorToken } from '@/pages/monitor/monitorClient';
import { c, font } from './theme';
import { useInstallPrompt } from './useInstallPrompt';

const TABS = [
  { to: '/app/privacy', label: 'Privacy', Icon: Shield },
  { to: '/app/money', label: 'Money', Icon: Wallet },
  { to: '/app/reminders', label: 'Reminders', Icon: Bell },
  { to: '/app/me', label: 'Me', Icon: User },
] as const;

function useIsWide(): boolean {
  const [wide, setWide] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : false,
  );
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px)');
    const on = () => setWide(mql.matches);
    mql.addEventListener('change', on);
    return () => mql.removeEventListener('change', on);
  }, []);
  return wide;
}

function NavItem({
  to,
  label,
  Icon,
  wide,
}: {
  to: string;
  label: string;
  Icon: typeof Shield;
  wide: boolean;
}) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        display: 'flex',
        flexDirection: wide ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: wide ? 'flex-start' : 'center',
        gap: wide ? 12 : 3,
        flex: wide ? '0 0 auto' : '1 1 0',
        minHeight: 44,
        padding: wide ? '12px 16px' : '8px 0',
        borderRadius: wide ? 12 : 0,
        textDecoration: 'none',
        color: isActive ? c.accent : c.inkSubtle,
        background: wide && isActive ? c.accentSoft : 'transparent',
        fontWeight: isActive ? 700 : 600,
        fontSize: wide ? 15 : 11,
      })}
    >
      {({ isActive }) => (
        <>
          <Icon size={wide ? 20 : 22} strokeWidth={isActive ? 2.4 : 2} />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
}

function InstallBanner() {
  const { canInstall, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(false);
  if (!canInstall || dismissed) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        margin: '12px 18px 0',
        padding: '12px 14px',
        background: c.accent,
        color: '#ffffff',
        borderRadius: 14,
      }}
    >
      <Download size={20} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800 }}>Install Monitor</div>
        <div style={{ fontSize: 12, opacity: 0.9 }}>Add it to your home screen — opens like an app.</div>
      </div>
      <button
        type="button"
        onClick={() => promptInstall()}
        style={{
          flexShrink: 0,
          minHeight: 44,
          padding: '0 16px',
          fontSize: 14,
          fontWeight: 800,
          color: c.accent,
          background: '#ffffff',
          border: 'none',
          borderRadius: 10,
          cursor: 'pointer',
        }}
      >
        Install
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        style={{
          flexShrink: 0,
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          color: '#ffffff',
          cursor: 'pointer',
        }}
      >
        <X size={18} />
      </button>
    </div>
  );
}

export function AppShell() {
  const wide = useIsWide();
  const location = useLocation();
  const token = getMonitorToken();
  if (!token) return <Navigate to="/app/start" replace state={{ from: location.pathname }} />;

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: c.canvas,
        color: c.ink,
        fontFamily: font,
        display: wide ? 'grid' : 'block',
        gridTemplateColumns: wide ? '232px minmax(0, 1fr)' : undefined,
      }}
    >
      {wide && (
        <nav
          style={{
            position: 'sticky',
            top: 0,
            alignSelf: 'start',
            height: '100dvh',
            borderRight: `1px solid ${c.rim}`,
            background: c.surface,
            padding: '24px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: c.accent,
              padding: '0 16px 18px',
            }}
          >
            Monitor
          </div>
          {TABS.map((t) => (
            <NavItem key={t.to} {...t} wide />
          ))}
        </nav>
      )}

      <main
        style={{
          maxWidth: 560,
          margin: '0 auto',
          width: '100%',
          padding: wide ? '32px 24px 48px' : '0 0 80px',
          boxSizing: 'border-box',
        }}
      >
        <InstallBanner />
        <Outlet />
      </main>

      {!wide && (
        <nav
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            background: c.surface,
            borderTop: `1px solid ${c.rim}`,
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            zIndex: 20,
          }}
        >
          {TABS.map((t) => (
            <NavItem key={t.to} {...t} wide={false} />
          ))}
        </nav>
      )}
    </div>
  );
}

export default AppShell;
